import React, { useEffect } from "react";
import { Box, Tabs, Tab, Drawer, List, ListItemButton, Typography, IconButton, ListItemIcon, ListItemText, Divider, Collapse } from "@mui/material";
import { Settings, ChevronLeft, ChevronRight, Logout } from "@mui/icons-material";
import { Outlet, useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { useClientsQuery } from '../../../queries/useClientsQuery';
import { useAuth } from "../../../hooks/useAuth";
import { getRoleNameByKey } from "../../../utils/roleMap";
import ClientList from "../ClientList";
import { useClientStore } from "../../../stores/clientStore";
import { usePlanQuery } from "../../../queries/usePlanQuery";

const DRAWER_WIDTH_OPEN = 280;
const DRAWER_WIDTH_CLOSED = 60;

export default function ClientLayout() {
  const auth = useAuth();
  const { clientId } = useParams<{ clientId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(true);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);

  const outletBoxRef = React.useRef<HTMLDivElement | null>(null);
  const [boxHeight, setBoxHeight] = React.useState(0);

  useEffect(() => {
    if (outletBoxRef.current) {
      const updateHeight = () => {
        setBoxHeight(outletBoxRef?.current?.offsetHeight || 128);
      };

      updateHeight();

      window.addEventListener('resize', updateHeight);

      return () => {
        window.removeEventListener('resize', updateHeight);
      };
    }
  }, []);


  const drawerWidth = open ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED;

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };
  
  const { data: clients, isLoading: isClientsLoading, error: clientsError } = useClientsQuery();
  const { data: plans } = usePlanQuery(clientId);
  const {client, setClient, setPlans} = useClientStore();
  
  useEffect(() => {
    setClient(
      (clients || []).find(cl => cl._id == clientId)
    );
    setPlans(plans?.items || [])
  },[clientId, clients, plans]);

  //  CLEANUP
  const isClientAvailable = !isClientsLoading && !clientsError && client && clientId?.trim();
  const tabPaths = [
    { label: "Dashboard", path: "dashboard", enabled:isClientAvailable },
    { label: "Patients", path: "patients", enabled:isClientAvailable },
  ];

  const currentTab = tabPaths.findIndex(tab =>
    location.pathname.includes(`/${tab.path}`)
  );

  const userDisplayRole = React.useMemo(() => {
    return auth?.role ? getRoleNameByKey(auth?.role) : (auth?.role || 'ID');
  }, [auth]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const tab = tabPaths[newValue];
    if (tab && clientId) {
      navigate(`/v3/clients/${clientId}/${tab.path}`);
    }
  };

  const handleClientLogount = async () => {
    if(auth) {
      await auth?.logout();
      navigate('/');
    }
  }

  return (
    <Box display="flex" height="100vh">

      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            transition: (theme) =>
              theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            padding: (theme) => theme.spacing(0, 1),
            ...({ minHeight: 48 }),
          }}
        >
          {open && <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, pl:1 }}>Ziphy Outreach</Typography>}
          <IconButton onClick={open ? handleDrawerClose : handleDrawerOpen}>
            {open ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </Box>
        <Divider/>
        <List sx={{ flexGrow: 1 }}>

          <ListItemButton onClick={() => setUserMenuOpen(!userMenuOpen)} sx={{height:'4em'}}>
            <ListItemIcon>
              <img src="/logo64.png" alt="logo" style={{ width: 28, height: 28 }} />
            </ListItemIcon>
            {
              open && <ListItemText primary={auth?.user?.name}
                secondary={`${userDisplayRole}: ${auth?.user?.id}`}/>
            }
          </ListItemButton>
          <Collapse in={userMenuOpen && open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItemButton sx={{ pl: 4 }} onClick={handleClientLogount}>
                <ListItemIcon>
                  <Logout />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </List>
          </Collapse>
          <Divider/>
          <ClientList selected={clientId} clients={clients || []} loading={isClientsLoading}
            error={clientsError?.message}
            collapsed={drawerWidth == DRAWER_WIDTH_CLOSED}/>
        </List>
        <List>
          <ListItemButton onClick={() => navigate("/settings")}>
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            {open && "Menu"}
          </ListItemButton>
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        flex={1}
        marginLeft={0}
        padding="16px"
        sx={{
          display:'flex',
          flex:1,
          flexDirection: 'column',
          transition: (theme) =>
            theme.transitions.create("margin", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Box display="flex" alignItems="center" sx={{borderBottom:1, height:'48px'}} ref={outletBoxRef}>
          <Box
            sx={{
              flexBasis: "70%",
              minWidth: DRAWER_WIDTH_CLOSED + 200,
              maxWidth: "60%",
              pr: 2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              pb:0.5
            }}
          >
            <Typography variant="h6" noWrap>
              {client?.name || 'Client Dashboard'}
            </Typography>
          </Box>

          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{ flexGrow: 1, pb:0.5 }}
          >
            {tabPaths.map((tab) => (
              <Tab
                key={tab.path}
                label={tab.label}
                disabled={!tab.enabled}
                component={Link}
                to={`/v3/clients/${clientId}/${tab.path}`}
              />
            ))}
          </Tabs>
        </Box>

        <Box
          mt={2}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: `calc(100vh - ${boxHeight}px)`,
            overflowY: "auto",
          }}
        >
          <Outlet />
        </Box>
      </Box>

    </Box>
  );
}
